<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { FormKitMessages } from '@formkit/vue'
import { ToccoConferenceByIdQueryDocument, ToccoConferenceFieldsFragment } from '~/api/graphql/generated'
import { createMetaInfoObject } from '~/lib'

const { t } = useI18n()

useSeoMeta(
  createMetaInfoObject([
    ['title', `${t('seo.pageTitles.conferenceRegistration')} | Careum`],
    ['robots', 'noindex, nofollow'],
  ]),
)

const route = useRoute()

const { data } = await useAsyncQuery(ToccoConferenceByIdQueryDocument, { id: route.params.id })
const computedData = computed(() => data.value?.toccoConferenceById)

const formData = ref()
const showReview = ref(false)

const showCompanyAddress = ref(true)
const showInvoiceAddress = ref(false)

const hideCompany = computed({
  get: () => !showCompanyAddress.value,
  set: (checked: boolean) => {
    if (formData.value?.invoiceAddress?.invoiceAddressActive) {
      formData.value.invoiceAddress.invoiceAddressActive = 'privateAddress'
    }
    showCompanyAddress.value = !checked
  },
})

const handleSubmit = async () => {
  showReview.value = true
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const onCancel = () => {
  showReview.value = false
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<template>
  <Section class="grid register-form-layout">
    
<FormKitLazyProvider config-file="true">
<div class="ds-8 ds-o-1">
      <FormKit
        v-show="!showReview"
        v-model="formData"
        type="form"
        :actions="false"
        :incomplete-message="t('tocco.registration.conference.errors')"
        @submit="handleSubmit()"
      >
        <div class="form-title">
          <h6>
            {{ t('tocco.registration.conference.headings.subtitle') }}
          </h6>
          <h1>
            {{ t('tocco.registration.conference.headings.title') }}
          </h1>
          <p>
            {{ t('tocco.registration.conference.headings.description') }}
          </p>
        </div>

        <FormKit type="group" name="personalInfo">
          <h4>
            {{ t('tocco.registration.conference.personalInfo.title') }}
          </h4>
          <div class="form-section">
            <PersonalInfo />
            <Phones />
            <ProfessionalInfo
              :position-label="t('tocco.registration.conference.personalInfo.position')"
              :profession-label="t('tocco.registration.conference.personalInfo.profession')"
            />
          </div>
        </FormKit>

        <div class="separator" />

        <FormKit type="group" name="privateAddress">
          <h4>
            {{ t('tocco.registration.conference.privateAddress.title') }}
          </h4>
          <div class="form-section">
            <Address />
          </div>
        </FormKit>

        <div class="separator" />

        <FormKit type="group" name="company">
          <h4>{{ t('tocco.registration.conference.company.title') }}</h4>
          <div class="form-section">
            <Company v-if="showCompanyAddress" />

            <div class="hide-company-checkbox">
              <Checkbox
                id="conference-registration-hide-company"
                v-model="hideCompany"
                name="hideCompany"
                :label="t('tocco.registration.conference.company.hideCompany')"
                :required="false"
              />
            </div>
          </div>
        </FormKit>

        <div class="separator" />

        <h4>
          {{ t('tocco.registration.conference.multipleRegistration.title') }}
        </h4>
        <p>
          {{ t('tocco.registration.conference.multipleRegistration.description') }}
        </p>

        <div class="file-upload-container">
          <File
            id="conference-registration-upload"
            name="upload"
            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.xlt,.xla,.doc,.dot,.docx"
            :label="t('tocco.registration.conference.multipleRegistration.titleContainer')"
            placeholder="Datei auswählen"
            :required="false"
            :multiple="false"
            :custom-uploader="true"
          />
        </div>

        <div class="separator" />

        <FormKit type="group" name="invoiceAddress">
          <h4>
            {{ t('tocco.registration.conference.invoiceAddress.title') }}
          </h4>

          <div class="form-section">
            <Radio
              id="conference-registration-invoice-address-active"
              name="invoiceAddressActive"
              :options="[
                { label: t('tocco.registration.conference.invoiceAddress.privateAddress'), value: 'privateAddress' },
                {
                  label: t('tocco.registration.conference.invoiceAddress.company'),
                  value: 'company',
                  attrs: { disabled: !showCompanyAddress },
                },
                {
                  label: t('tocco.registration.conference.invoiceAddress.invoiceAddress'),
                  value: 'invoiceAddress',
                  attrs: { disabled: !showCompanyAddress },
                },
              ]"
              :required="true"
              @input="(value: string) => (showInvoiceAddress = value === 'invoiceAddress')"
            />

            <Company v-if="showInvoiceAddress" :has-department-billing-information="true" />
          </div>
        </FormKit>

        <div class="separator" />

        <h4>
          {{ t('tocco.registration.module.message.title') }}
        </h4>

        <div class="form-section">
          <Textarea
            id="conference-registration-message"
            name="message"
            :label="t('tocco.registration.conference.message.label')"
            :required="false"
          />
        </div>

        <div class="buttons-wrapper">
          <FormKitMessages />
          <Button primary slide class="submit-button">
            {{ t('tocco.registration.module.submit') }}
          </Button>
        </div>
      </FormKit>

      <ToccoRegistrationConferenceReview
        v-if="showReview && formData"
        :form-data="formData"
        :entity="computedData as ToccoConferenceFieldsFragment"
        @cancel="onCancel"
      />
    </div>
</FormKitLazyProvider>

  </Section>
</template>

<style lang="scss" scoped>
.form-title {
  margin-bottom: 75px;

  @include breakpoint(tl) {
    margin-bottom: 95px;
  }

  h6 {
    margin-bottom: 9px;
  }

  h1 {
    margin-bottom: 0;
  }

  .asterisk {
    font-size: 14px;
  }
}

.hide-company-checkbox {
  margin-top: calc(32px - var(--form-element-gap));
}

.buttons-wrapper {
  margin-top: 32px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 30px;

  @include breakpoint(tl) {
    align-items: center;
    flex-direction: row;
  }

  .submit-button {
    margin-left: auto;
  }
}
</style>
